import { splitWords } from '../../../lib/wordNotes'

// ─── Per-word notes (the pre-dictionary feature, read-only since V2-p) ────────

/**
 * One text input per word of a sentence's *current* textEn (owner request,
 * replacing the practice screen's whole-sentence translation toggle — no
 * go-live-plan item). A word's position in `textEn.split(/\s+/)` is its
 * `index` on the wire — the exact same tokenization rule the backend
 * validates (admin-lessons.service.ts) and the PWA practice page uses to
 * line notes up with words (pages/Lesson.jsx via TappableSentence). Kept
 * here as a sparse `{ index, note }[]`; only non-empty notes are sent.
 *
 * Since the shared dictionary (V2-p) the panel authors word links instead
 * (`WordLinkInputs`), and this component is only used read-only: it shows
 * the notes a sentence already has, so nothing an admin wrote disappears
 * before `wordNotes` leaves the wire in a later release. With `readOnly`
 * it renders nothing at all when there are no notes.
 *
 * Recomputes the word list from `textEn` on every render, so it never goes
 * stale — the caller is responsible for pruning `wordNotes` when `textEn`
 * shrinks, this component just renders whatever indices currently exist.
 */
export function WordNoteInputs({ textEn, wordNotes, onChange, readOnly = false }) {
  if (!textEn.trim()) return null
  const words = splitWords(textEn)
  const noteFor = (index) => wordNotes.find((entry) => entry.index === index)?.note ?? ''

  if (readOnly) {
    const noted = words.map((word, index) => ({ word, index, note: noteFor(index) })).filter((item) => item.note)
    if (noted.length === 0) return null
    return (
      <div className="rounded-lg border border-dashed border-slate-200 p-2" data-word-notes-readonly>
        <p className="mb-1 text-[10px] text-slate-400">یادداشت‌های قدیمی این جمله (فقط‌خواندنی — از این پس کلمه‌ها را به واژه‌نامه پیوند دهید):</p>
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
          {noted.map(({ word, index, note }) => (
            <li key={index}>
              <span dir="ltr" className="font-medium text-slate-700">{word}</span>
              <span className="text-slate-400"> — </span>
              <span>{note}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  function setNote(index, value) {
    const rest = wordNotes.filter((entry) => entry.index !== index)
    onChange(value === '' ? rest : [...rest, { index, note: value }].sort((a, b) => a.index - b.index))
  }

  return (
    <div className="flex flex-wrap gap-1.5 rounded-lg bg-slate-50 p-2">
      {words.map((word, index) => (
        <label key={index} className="flex flex-col gap-0.5">
          <span className="ltr truncate px-0.5 text-[10px] text-slate-400" title={word}>
            {word || '·'}
          </span>
          <input
            value={noteFor(index)}
            onChange={(e) => setNote(index, e.target.value)}
            placeholder="یادداشت این کلمه…"
            dir="rtl"
            className="w-28 rounded border border-slate-200 px-1.5 py-1 text-xs focus:border-brand-500 focus:outline-none"
          />
        </label>
      ))}
    </div>
  )
}
